import React from 'react';
import { useLocation } from 'react-router-dom';

import Tabs, { Tab } from '../../library/tabs/tabs';
import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';

import { useFitment } from '../../shared/hooks/hooks';

import { getTabURLParams } from '../../shared/utils/url-params/url-params';

import { PRODUCT_TYPES } from '../../shared/constants/products';
import { STAGGERED_SEARCH_TYPES } from '../../shared/constants/staggered-products';

import './staggered-product-tabs.scss';

interface TireSizeDetails {
  aspectRatio: string;
  diameter: string;
  width: string;
}

interface TireSize {
  front: TireSizeDetails | null | undefined;
  rear: TireSizeDetails | null | undefined;
}

interface WheelSizeDetails {
  boltPattern: string;
  diameter: string;
  width: string;
}

interface WheelSizes {
  front: WheelSizeDetails | null;
  rear: WheelSizeDetails | null | undefined;
}

interface StaggeredProductTabsProps {
  className?: string;
  isSizeSearch?: boolean;
  onTabClick?: (searchType: string | undefined, searchTypeSet: string) => void;
  productType: string;
  renderToolTip?: (value: string) => void;
  staggeredSearchType?: string;
  tireSizes: TireSize | null;
  wheelSizes: WheelSizes | null;
}

function StaggeredProductTabs(props: StaggeredProductTabsProps) {
  const {
    className = '',
    productType,
    isSizeSearch,
    staggeredSearchType,
    tireSizes,
    wheelSizes,
    onTabClick,
    renderToolTip
  } = props;
  const isTireTabs = productType === PRODUCT_TYPES.TIRES;

  const { pathname, search } = useLocation();
  const { selectedFitment } = useFitment();

  const {
    frontTireSize,
    frontWheelDiameter,
    rearTireSize,
    rearWheelDiameter,
    isOE
  } = selectedFitment || {};

  let basePath = '';
  if (pathname.includes('optionalPlusSet') || isSizeSearch) {
    basePath =
      staggeredSearchType !== STAGGERED_SEARCH_TYPES.SET
        ? pathname.replace(/\/front|\/rear$/, '')
        : pathname;
  } else {
    basePath =
      staggeredSearchType !== STAGGERED_SEARCH_TYPES.SET
        ? pathname.replace(/\/staggered.+/, '')
        : pathname.replace('/sets', '');
  }

  const urlParams = getTabURLParams(search);
  let frontProductSize = '';
  let rearProductSize = '';

  if (isTireTabs) {
    const { front, rear } = tireSizes || {};

    frontProductSize = isSizeSearch
      ? `${front?.width}/${front?.aspectRatio}-${front?.diameter}`
      : frontTireSize;
    rearProductSize = isSizeSearch
      ? `${rear?.width}/${rear?.aspectRatio}-${rear?.diameter}`
      : rearTireSize;
  } else {
    const { front, rear } = wheelSizes || {};

    frontProductSize = isSizeSearch
      ? `${front?.diameter}`
      : `${frontWheelDiameter}"`;
    rearProductSize = isSizeSearch
      ? `${rear?.diameter}`
      : `${rearWheelDiameter}"`;
  }
  const tooltipContent =
    'Your vehicle uses different tire sizes for front and rear tires. We recommend you shop by set if you wish to replace all 4.';

  return (
    <div
      className={`staggered-product-tabs ${className}`}
      styleName="container"
    >
      <hr className="staggered-product-tabs__divider" styleName="divider" />
      <Tabs className="staggered-product-tabs__tabs">
        <Tab
          className="staggered-product-tabs__tab-container"
          href={`${basePath}${
            isOE && !isSizeSearch ? '/sets' : ''
          }${urlParams}`}
          isSelected={staggeredSearchType === STAGGERED_SEARCH_TYPES.SET}
          onClick={() =>
            onTabClick?.(staggeredSearchType, STAGGERED_SEARCH_TYPES.SET)
          }
          styleName="tab-container"
        >
          <Typography styleName="staggered-sets-label" weight="heavy">
            Sets{' '}
            {renderToolTip ? (
              renderToolTip(tooltipContent)
            ) : (
              <Tooltip
                content={tooltipContent}
                styleName="staggered-sets-tooltip"
              />
            )}
          </Typography>
        </Tab>
        <Tab
          className="staggered-product-tabs__tab-container"
          href={`${basePath}${
            isOE && !isSizeSearch ? '/staggered' : ''
          }/front${urlParams}`}
          isSelected={staggeredSearchType === STAGGERED_SEARCH_TYPES.FRONT}
          onClick={() =>
            onTabClick?.(staggeredSearchType, STAGGERED_SEARCH_TYPES.FRONT)
          }
          styleName="tab-container"
        >
          <div styleName="staggered-tire-label">
            <Typography weight="heavy">Front</Typography>
            <Typography size="small">{frontProductSize}</Typography>
          </div>
        </Tab>
        <Tab
          className="staggered-product-tabs__tab-container"
          href={`${basePath}${
            isOE && !isSizeSearch ? '/staggered' : ''
          }/rear${urlParams}`}
          isSelected={staggeredSearchType === STAGGERED_SEARCH_TYPES.REAR}
          onClick={() =>
            onTabClick?.(staggeredSearchType, STAGGERED_SEARCH_TYPES.REAR)
          }
          styleName="tab-container"
        >
          <div styleName="staggered-tire-label">
            <Typography weight="heavy">Rear</Typography>
            <Typography size="small">{rearProductSize}</Typography>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

export default StaggeredProductTabs;
